import type { MouseEventHandler } from 'react'
import { useCallback } from 'react'
import useSetImmersiveMode from './useImmersiveModeHandlers'
import useTripClientSource from './useTripClientSource'
import BookHotelFlights from '../components/TripModal/RestoreCart/BookHotelFlight'
import { getRestoreCartUriForId } from 'checkout/lib/utils/misc/restoreCartUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import noop from 'lib/function/noop'
import { getCurrentUserId } from 'selectors/accountSelectors'
import BookTourFlight from 'tripPlanner/components/TripModal/RestoreCart/BookTourFlight'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { useExperience } from './api/offer'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { LETripItem } from 'tripPlanner/types/tripItem'
import {
  buildProductLinkWithQueryParams,
  isBookableHotelFlightBundle,
  isBookableTourV1FlightBundle,
} from 'tripPlanner/utils/bookmark/restoreCart'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import useOffer from 'hooks/Offers/useOffer'
import { getCartItems } from 'api/cart/getCartItems'

export const BOOK_NOW_BUTTON_TEXT = 'Book now'

type ReturnProps = {
  label: string | undefined
  onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  productLink: string | undefined
  canRestoreCart: boolean
}

const offerSourceTypes = new Set<LETripItem['sourceType']>([
  'LE_Hotel',
  'LE_Hotel_Bedbank',
  'LE_Tour',
  'LE_Cruise',
  'LE_Accommodation_Rental',
])

export default function useRestoreCartButtonProps(
  item: LETripItem,
): ReturnProps {
  let offerId: string | undefined = undefined
  if (offerSourceTypes.has(item.sourceType)) {
    offerId = item.code
  } else if (item.type === 'FLIGHT' && item.savedItemData?.hotelBundle) {
    offerId = item.savedItemData.hotelBundle.offerId
  } else if (item.type === 'FLIGHT' && item.savedItemData?.tourBundle) {
    offerId = item.savedItemData.tourBundle.offerId
  }
  const [offer] = useOffer(offerId)
  const { data: experience } = useExperience({
    currentCurrency: item.savedItemData.currencyCode,
    currentRegionCode: item.savedItemData.regionCode,
    experienceId: item.sourceType === 'LE_Experience' ? item.code : undefined,
  })

  const currentUserId = useAppSelector(getCurrentUserId)

  const canEdit = useCanEditTrip()

  const { setImmersiveMode } = useSetImmersiveMode()
  const openModal = useOpenModal()

  const source = useTripClientSource()

  const productLink = buildProductLinkWithQueryParams(
    item,
    { offer, experience },
    { source },
  )

  const navigateTo = useNavigateTo()

  const modalElement = useModalElementContext()
  const onClick = useCallback(
    async(e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const canBookOfferStandalone =
        offer && !(offer as App.Offer).bundledWithFlightsOnly

      if (item.savedItemData.cartId) {
        let canRestoreCart = false
        await getCartItems(item.savedItemData.cartId)
          .then(() => {
            canRestoreCart = true
          })
          .catch(noop)

        if (canRestoreCart) {
          const restoreCartUrl = getRestoreCartUriForId(
            item.savedItemData.cartId,
          )

          if (
            item.sourceType !== 'LE_Accommodation_Rental' &&
            isBookableHotelFlightBundle(item) &&
            canBookOfferStandalone
          ) {
            openModal(BookHotelFlights, { item, restoreCartUrl })
          } else if (
            isBookableTourV1FlightBundle(item) &&
            canBookOfferStandalone
          ) {
            openModal(BookTourFlight, { item, restoreCartUrl })
          } else {
            navigateTo(restoreCartUrl)
          }
          return
        }
      }

      if (productLink) {
        if (canEdit) {
          setImmersiveMode({
            tripId: item.tripId,
            sectionId: tripItemSelectionId(item.id),
          })
        }

        navigateTo(productLink)
      }
      modalElement?.resolve()
    },
    [
      canEdit,
      item,
      modalElement,
      navigateTo,
      offer,
      openModal,
      productLink,
      setImmersiveMode,
    ],
  )

  const canRestoreCart =
    !!productLink &&
    !!item.savedItemData.cartId &&
    currentUserId === item.createdBy

  return {
    label: BOOK_NOW_BUTTON_TEXT,
    canRestoreCart,
    onClick,
    productLink,
  }
}
